import React, { FormEvent, useEffect, useRef, useState } from 'react';
import {
  Check,
  Clock3,
  Copy,
  KeyRound,
  Link2,
  Loader2,
  Mail,
  ShieldCheck,
  Trash2,
  UserCheck,
  X,
} from 'lucide-react';

const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const API_BASE = isLocalhost ? 'http://localhost:5000' : '';

interface AvatarShareDialogProps {
  isOpen: boolean;
  onClose: () => void;
  isDarkMode: boolean;
  customerSlug: string;
  workspaceSlug?: string;
  agentSlug: string;
  agentName: string;
  portraitUrl?: string;
}

interface ShareInvitation {
  id: string;
  recipientEmail?: string | null;
  expiresAt?: string | null;
  status?: string;
  maxUses?: number;
  useCount?: number;
  usesRemaining?: number | null;
  createdAt?: string | null;
}

interface AvatarGrant {
  id: string;
  email?: string | null;
  username?: string | null;
  status?: string;
  acceptedAt?: string | null;
  createdAt?: string | null;
}

const normalizeInvitation = (raw: any): ShareInvitation => ({
  id: String(raw?.id || raw?.invitationId || ''),
  recipientEmail: raw?.recipientEmail ?? raw?.recipient_email ?? null,
  expiresAt: raw?.expiresAt ?? raw?.expires_at ?? null,
  status: raw?.status || 'active',
  maxUses: Number(raw?.maxUses ?? raw?.max_uses ?? 0) || undefined,
  useCount: Number(raw?.useCount ?? raw?.use_count ?? 0),
  usesRemaining: raw?.usesRemaining ?? raw?.uses_remaining ?? null,
  createdAt: raw?.createdAt ?? raw?.created_at ?? null,
});

const normalizeGrant = (raw: any): AvatarGrant => ({
  id: String(raw?.id || raw?.grantId || ''),
  email: raw?.email ?? raw?.userEmail ?? raw?.user_email ?? null,
  username: raw?.username ?? raw?.userName ?? raw?.user_name ?? null,
  status: raw?.status || 'active',
  acceptedAt: raw?.acceptedAt ?? raw?.accepted_at ?? null,
  createdAt: raw?.createdAt ?? raw?.created_at ?? null,
});

const formatDate = (value?: string | null): string => {
  if (!value) return 'Ohne Datum';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return new Intl.DateTimeFormat('de-DE', { dateStyle: 'medium', timeStyle: 'short' }).format(parsed);
};

const statusLabel = (status?: string): string => {
  switch ((status || '').toLowerCase()) {
    case 'active': return 'Aktiv';
    case 'accepted': return 'Angenommen';
    case 'expired': return 'Abgelaufen';
    case 'revoked': return 'Widerrufen';
    case 'exhausted': return 'Aufgebraucht';
    default: return status || 'Unbekannt';
  }
};

const invitationStatusClasses = (status: string | undefined, isDarkMode: boolean): string => {
  switch ((status || '').toLowerCase()) {
    case 'active':
      return isDarkMode ? 'border-blue-400/30 bg-blue-950/40 text-blue-100' : 'border-blue-200 bg-blue-50 text-blue-700';
    case 'expired':
    case 'exhausted':
      return isDarkMode ? 'border-amber-400/30 bg-amber-950/30 text-amber-100' : 'border-amber-200 bg-amber-50 text-amber-800';
    default:
      return isDarkMode ? 'border-red-400/25 bg-red-950/25 text-red-100' : 'border-red-200 bg-red-50 text-red-700';
  }
};

const AvatarShareDialog: React.FC<AvatarShareDialogProps> = ({
  isOpen,
  onClose,
  isDarkMode,
  customerSlug,
  workspaceSlug = 'main',
  agentSlug,
  agentName,
  portraitUrl,
}) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [expiresDays, setExpiresDays] = useState(14);
  const [maxUses, setMaxUses] = useState(100);
  const [invitations, setInvitations] = useState<ShareInvitation[]>([]);
  const [grants, setGrants] = useState<AvatarGrant[]>([]);
  const [shareUrl, setShareUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [actionId, setActionId] = useState('');
  const [error, setError] = useState('');

  const authHeaders = (withJson = false): HeadersInit => {
    const token = window.sessionStorage.getItem('admin_token');
    return {
      ...(withJson ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  };

  const loadSharing = async () => {
    setIsLoading(true);
    setError('');
    try {
      const query = new URLSearchParams({ customer: customerSlug, workspace: workspaceSlug, agent: agentSlug });
      const response = await fetch(`${API_BASE}/api/admin/avatar-shares?${query.toString()}`, {
        headers: authHeaders(),
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok) throw new Error(payload?.error || 'Freigaben konnten nicht geladen werden.');
      setInvitations(Array.isArray(payload?.invitations) ? payload.invitations.map(normalizeInvitation) : []);
      setGrants(
        Array.isArray(payload?.grants)
          ? payload.grants.map(normalizeGrant).filter((grant: AvatarGrant) => grant.status === 'active')
          : [],
      );
    } catch (loadError: any) {
      setError(loadError?.message || 'Freigaben konnten nicht geladen werden.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    previouslyFocusedRef.current = document.activeElement as HTMLElement | null;
    setShareUrl('');
    setCopied(false);
    void loadSharing();
    window.setTimeout(() => closeButtonRef.current?.focus(), 0);

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== 'Tab' || !dialogRef.current) return;
      const focusable = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input:not([disabled]), select:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])',
      ));
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = originalOverflow;
      previouslyFocusedRef.current?.focus();
    };
    // loadSharing is intentionally scoped to the currently opened avatar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentSlug, customerSlug, isOpen, onClose, workspaceSlug]);

  if (!isOpen) return null;

  const handleCreate = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');
    setCopied(false);
    setIsCreating(true);
    try {
      const response = await fetch(`${API_BASE}/api/admin/avatar-shares`, {
        method: 'POST',
        headers: authHeaders(true),
        body: JSON.stringify({
          customerSlug,
          workspaceSlug,
          agentSlug,
          ...(recipientEmail.trim() ? { recipientEmail: recipientEmail.trim() } : {}),
          expiresDays,
          maxUses,
        }),
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok) throw new Error(payload?.error || 'Einladungslink konnte nicht erstellt werden.');
      setShareUrl(String(payload?.shareUrl || ''));
      if (payload?.invitation) {
        setInvitations((current) => [normalizeInvitation(payload.invitation), ...current]);
      } else {
        await loadSharing();
      }
    } catch (createError: any) {
      setError(createError?.message || 'Einladungslink konnte nicht erstellt werden.');
    } finally {
      setIsCreating(false);
    }
  };

  const handleCopy = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
    } catch {
      const input = document.getElementById('avatar-share-url') as HTMLInputElement | null;
      input?.select();
      document.execCommand('copy');
    }
    setCopied(true);
  };

  const handleRevokeInvitation = async (id: string) => {
    setActionId(`invitation:${id}`);
    setError('');
    try {
      const response = await fetch(`${API_BASE}/api/admin/avatar-shares/${encodeURIComponent(id)}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok) throw new Error(payload?.error || 'Einladung konnte nicht widerrufen werden.');
      setInvitations((current) => current.map((item) => item.id === id ? { ...item, status: 'revoked' } : item));
    } catch (revokeError: any) {
      setError(revokeError?.message || 'Einladung konnte nicht widerrufen werden.');
    } finally {
      setActionId('');
    }
  };

  const handleRevokeGrant = async (id: string) => {
    setActionId(`grant:${id}`);
    setError('');
    try {
      const response = await fetch(`${API_BASE}/api/admin/avatar-access/${encodeURIComponent(id)}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok) throw new Error(payload?.error || 'Zugriff konnte nicht entzogen werden.');
      setGrants((current) => current.filter((item) => item.id !== id));
    } catch (revokeError: any) {
      setError(revokeError?.message || 'Zugriff konnte nicht entzogen werden.');
    } finally {
      setActionId('');
    }
  };

  const shellClasses = isDarkMode ? 'border-white/10 bg-slate-950 text-slate-50' : 'border-slate-200 bg-white text-slate-950';
  const panelClasses = isDarkMode ? 'border-white/10 bg-slate-900/70' : 'border-slate-200 bg-slate-50';
  const inputClasses = isDarkMode
    ? 'border-slate-700 bg-slate-950 text-slate-50 focus:border-blue-400'
    : 'border-slate-300 bg-white text-slate-950 focus:border-blue-500';
  const mutedClasses = isDarkMode ? 'text-slate-300' : 'text-slate-600';

  return (
    <div className="fixed inset-0 z-[70] flex items-end justify-center bg-slate-950/65 p-0 sm:items-center sm:p-6" onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose();
    }}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="avatar-share-title"
        className={`max-h-[96vh] w-full max-w-4xl overflow-y-auto border shadow-2xl sm:max-h-[90vh] sm:rounded-xl ${shellClasses}`}
      >
        <header className={`sticky top-0 z-10 flex items-start justify-between gap-4 border-b px-5 py-4 sm:px-6 ${isDarkMode ? 'border-white/10 bg-slate-950' : 'border-slate-200 bg-white'}`}>
          <div className="flex min-w-0 items-center gap-4">
            <div className={`flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-lg border ${panelClasses}`}>
              {portraitUrl ? <img src={portraitUrl} alt="" className="h-full w-full object-cover" /> : <KeyRound className="h-5 w-5 text-blue-500" />}
            </div>
            <div className="min-w-0">
              <p className={`text-[11px] font-bold uppercase tracking-[0.2em] ${mutedClasses}`}>Avatar-Zugang</p>
              <h2 id="avatar-share-title" className="mt-1 break-words text-xl font-semibold">{agentName} teilen</h2>
              <p className={`mt-1 text-sm ${mutedClasses}`}>Zugriff nur auf diesen Avatar</p>
            </div>
          </div>
          <button ref={closeButtonRef} type="button" onClick={onClose} aria-label="Dialog schließen" className={`rounded-lg border p-2 transition ${isDarkMode ? 'border-white/10 hover:bg-white/10' : 'border-slate-200 hover:bg-slate-100'}`}>
            <X className="h-5 w-5" />
          </button>
        </header>

        <div className="space-y-7 px-5 py-6 sm:px-6">
          <div className={`flex flex-col items-start gap-2 rounded-lg border px-4 py-3 sm:grid sm:grid-cols-[auto_1fr_auto_1fr_auto] sm:items-center ${panelClasses}`} aria-label="Berechtigungsumfang">
            <span className="break-words text-xs font-semibold">{customerSlug}</span><span className="hidden h-px bg-blue-500/40 sm:block" />
            <span className="break-words text-xs font-semibold text-blue-600 dark:text-blue-300">{agentName}</span><span className="hidden h-px bg-blue-500/40 sm:block" />
            <span className="flex items-center gap-1 text-xs font-semibold"><ShieldCheck className="h-4 w-4 text-emerald-500" /> 1 Zugang</span>
          </div>

          <section aria-labelledby="create-share-title">
            <div className="mb-4">
              <h3 id="create-share-title" className="text-base font-semibold">Neuen Einladungslink erstellen</h3>
              <p className={`mt-1 text-sm ${mutedClasses}`}>E-Mail-Bindung ist optional. Ohne Bindung kann jede Person mit dem Link ein Konto verknüpfen.</p>
            </div>
            <form onSubmit={handleCreate} className={`grid gap-4 rounded-lg border p-4 sm:grid-cols-2 lg:grid-cols-[1fr_160px_140px_auto] ${panelClasses}`}>
              <label className="space-y-1.5 sm:col-span-2 lg:col-span-1">
                <span className="text-xs font-semibold">E-Mail des Empfängers <span className={mutedClasses}>(optional)</span></span>
                <div className="relative">
                  <Mail className={`absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 ${mutedClasses}`} />
                  <input type="email" value={recipientEmail} onChange={(event) => setRecipientEmail(event.target.value)} placeholder="kunde@beispiel.de" className={`w-full rounded-lg border py-2.5 pl-9 pr-3 text-sm outline-none ${inputClasses}`} />
                </div>
              </label>
              <label className="space-y-1.5">
                <span className="text-xs font-semibold">Gültigkeit</span>
                <select value={expiresDays} onChange={(event) => setExpiresDays(Number(event.target.value))} className={`w-full rounded-lg border px-3 py-2.5 text-sm outline-none ${inputClasses}`}>
                  <option value={1}>1 Tag</option><option value={7}>7 Tage</option><option value={14}>14 Tage</option><option value={30}>30 Tage</option><option value={90}>90 Tage</option>
                </select>
              </label>
              <label className="space-y-1.5">
                <span className="text-xs font-semibold">Max. Nutzungen</span>
                <input type="number" min={1} max={10000} value={maxUses} onChange={(event) => setMaxUses(Number(event.target.value))} className={`w-full rounded-lg border px-3 py-2.5 text-sm outline-none ${inputClasses}`} />
              </label>
              <button type="submit" disabled={isCreating || maxUses < 1} className="self-end inline-flex min-h-[42px] items-center justify-center gap-2 rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-60 sm:col-span-2 lg:col-span-1">
                {isCreating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
                Erstellen
              </button>
            </form>
          </section>

          {shareUrl ? (
            <section className={`border-l-4 border-emerald-500 p-4 ${isDarkMode ? 'bg-emerald-950/25' : 'bg-emerald-50'}`} aria-live="polite">
              <div className="flex items-start gap-3">
                <Check className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
                <div className="min-w-0 flex-1">
                  <h3 className="font-semibold text-emerald-700 dark:text-emerald-200">Einladungslink bereit</h3>
                  <p className={`mt-1 text-xs ${mutedClasses}`}>Dieser vollständige Link wird aus Sicherheitsgründen nur jetzt angezeigt.</p>
                  <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                    <input id="avatar-share-url" readOnly value={shareUrl} aria-label="Einladungslink" className={`min-w-0 flex-1 rounded-lg border px-3 py-2 text-sm outline-none ${inputClasses}`} />
                    <button type="button" onClick={handleCopy} className={`inline-flex items-center justify-center gap-2 rounded-lg border px-4 py-2 text-sm font-semibold transition ${copied ? 'border-emerald-500 bg-emerald-600 text-white' : isDarkMode ? 'border-white/15 hover:bg-white/10' : 'border-slate-300 hover:bg-white'}`}>
                      {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                      {copied ? 'Kopiert' : 'Kopieren'}
                    </button>
                  </div>
                </div>
              </div>
            </section>
          ) : null}

          {error ? <div role="alert" className="border-l-4 border-red-500 bg-red-50 p-4 text-sm text-red-800 dark:bg-red-950/30 dark:text-red-100">{error}</div> : null}

          <section aria-labelledby="share-history-title">
            <div className="mb-4 flex items-end justify-between gap-4">
              <div><h3 id="share-history-title" className="text-base font-semibold">Einladungen</h3><p className={`mt-1 text-sm ${mutedClasses}`}>Status, Laufzeit und verbleibende Nutzungen.</p></div>
              {isLoading ? <Loader2 aria-label="Einladungen werden geladen" className="h-5 w-5 animate-spin text-blue-500" /> : null}
            </div>
            {!isLoading && invitations.length === 0 ? (
              <div className={`rounded-lg border border-dashed p-5 text-center text-sm ${panelClasses} ${mutedClasses}`}>Noch keine Einladungslinks für diesen Avatar.</div>
            ) : (
              <div className="space-y-2">
                {invitations.map((invitation) => {
                  const isActive = invitation.status?.toLowerCase() === 'active';
                  const remaining = invitation.usesRemaining ?? (invitation.maxUses != null ? Math.max(0, invitation.maxUses - (invitation.useCount || 0)) : null);
                  return (
                    <article key={invitation.id} className={`grid gap-3 rounded-lg border p-4 sm:grid-cols-[1fr_auto_auto] sm:items-center ${panelClasses}`}>
                      <div className="min-w-0"><p className="truncate text-sm font-semibold">{invitation.recipientEmail || 'Offener Einladungslink'}</p><p className={`mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs ${mutedClasses}`}><span className="inline-flex items-center gap-1"><Clock3 className="h-3.5 w-3.5" /> bis {formatDate(invitation.expiresAt)}</span>{remaining != null ? <span>{remaining} Nutzungen übrig</span> : null}</p></div>
                      <span className={`w-fit rounded-md border px-2 py-1 text-xs font-semibold ${invitationStatusClasses(invitation.status, isDarkMode)}`}>{statusLabel(invitation.status)}</span>
                      <button type="button" disabled={!isActive || actionId === `invitation:${invitation.id}`} onClick={() => handleRevokeInvitation(invitation.id)} className="inline-flex items-center justify-center gap-2 rounded-lg border border-red-200 px-3 py-2 text-xs font-semibold text-red-700 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-red-400/25 dark:text-red-200 dark:hover:bg-red-950/30">
                        {actionId === `invitation:${invitation.id}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />} Widerrufen
                      </button>
                    </article>
                  );
                })}
              </div>
            )}
          </section>

          <section aria-labelledby="grants-title">
            <div className="mb-4"><h3 id="grants-title" className="text-base font-semibold">Zugelassene Benutzer</h3><p className={`mt-1 text-sm ${mutedClasses}`}>Diese Konten dürfen ausschließlich {agentName} verwenden.</p></div>
            {!isLoading && grants.length === 0 ? (
              <div className={`rounded-lg border border-dashed p-5 text-center text-sm ${panelClasses} ${mutedClasses}`}>Noch niemand hat eine Einladung angenommen.</div>
            ) : (
              <div className="space-y-2">
                {grants.map((grant) => (
                  <article key={grant.id} className={`flex flex-col gap-3 rounded-lg border p-4 sm:flex-row sm:items-center sm:justify-between ${panelClasses}`}>
                    <div className="flex min-w-0 items-center gap-3"><UserCheck className="h-5 w-5 shrink-0 text-emerald-500" /><div className="min-w-0"><p className="truncate text-sm font-semibold">{grant.email || grant.username || 'Benutzer'}</p>{grant.email && grant.username ? <p className={`truncate text-xs ${mutedClasses}`}>{grant.username}</p> : null}<p className={`mt-1 text-xs ${mutedClasses}`}>Freigabe seit {formatDate(grant.acceptedAt || grant.createdAt)}</p></div></div>
                    <button type="button" disabled={actionId === `grant:${grant.id}`} onClick={() => handleRevokeGrant(grant.id)} className="inline-flex items-center justify-center gap-2 rounded-lg border border-red-200 px-3 py-2 text-xs font-semibold text-red-700 transition hover:bg-red-50 disabled:opacity-50 dark:border-red-400/25 dark:text-red-200 dark:hover:bg-red-950/30">
                      {actionId === `grant:${grant.id}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />} Zugriff entziehen
                    </button>
                  </article>
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default AvatarShareDialog;
